/* import __COLOCATED_TEMPLATE__ from './change-ticket-type-panel.hbs'; */
/* RESPONSIBLE TEAM: team-inbox */
import Component from '@glimmer/component';
import { TicketTypeResource } from 'embercom/components/inbox2/ticket-type-resource';
import { useResource } from 'ember-resources';
import { task } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import type Snackbar from 'embercom/services/snackbar';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import type Conversation from 'embercom/objects/inbox/conversation';
import type InboxApi from 'embercom/services/inbox-api';
import type TicketAttributeSummary from 'embercom/objects/inbox/ticket-attribute-summary';
import type InboxState from 'embercom/services/inbox-state';
import { action } from '@ember/object';
import type LinkedTicketsResource from './linked-tickets-resource';

export interface ChangeTicketTypePanelArgs {
  originalConversation: Conversation;
  newTicketTypeId?: number;
  hidePanel: () => void;
  linkedTicketsResource: LinkedTicketsResource;
}

interface Signature {
  Args: ChangeTicketTypePanelArgs;
}

export default class ChangeTicketTypePanel extends Component<Signature> {
  @service declare snackbar: Snackbar;
  @service declare inboxApi: InboxApi;
  @service declare intl: IntlService;
  @service declare inboxState: InboxState;

  readonly ticketTypeResource = useResource(this, TicketTypeResource, () => ({
    ticketTypeId: this.args.newTicketTypeId,
    conversation: this.args.originalConversation,
    matchAttributesByNameAndType: true,
  }));

  get shouldShowAttributes() {
    let hasSelectedType = !!this.ticketTypeResource.selectedTicketType;
    let hasAttributes = this.ticketTypeResource.ticketHasAttributes;
    let isLoading = this.ticketTypeResource.isLoading;
    return hasSelectedType && hasAttributes && !isLoading;
  }

  @task({ drop: true })
  *changeTicketType(): any {
    if (this.args.newTicketTypeId === undefined) {
      return;
    }

    if (this.ticketTypeResource.missingRequiredAttributes.length > 0) {
      let errorMessage = this.intl.t('inbox.new-conversation.errors.invalid-ticket-attributes', {
        required_attributes: this.ticketTypeResource.missingRequiredAttributes
          .map((attr) => attr.descriptor.name)
          .join(', '),
      });
      this.snackbar.notifyError(errorMessage);
      return;
    }

    if (this.ticketTypeResource.attributesWithInvalidTypes.length > 0) {
      let errorMessage = this.intl.t(
        'inbox.new-conversation.errors.invalid-ticket-attributes-types',
        {
          invalid_attributes: this.ticketTypeResource.attributesWithInvalidTypes
            .map((attr) => attr.descriptor.name)
            .join(', '),
        },
      );
      this.snackbar.notifyError(errorMessage);
      return;
    }

    let ticketAttributes = this.ticketTypeResource.ticketAttributes?.map(
      (attributeSummary: TicketAttributeSummary) => {
        return {
          descriptor_id: attributeSummary.descriptor.id,
          value: attributeSummary.serializedValue,
        };
      },
    );

    // TODO: Implement backend endpoint for changing ticket type
    // For now, just log what would be sent to the API and close panel
    // eslint-disable-next-line no-console
    console.log('Change ticket type request:', {
      conversationId: this.args.originalConversation.id,
      ticketTypeId: this.args.newTicketTypeId,
      ticketAttributes,
    });

    // Placeholder yield - will be replaced with actual API call
    yield Promise.resolve();

    this.args.hidePanel();
  }

  get isChangingTicketType() {
    return taskFor(this.changeTicketType).isRunning;
  }

  @action
  noopCompanySelection(_companyId: string | null, _isDefaultSelection: boolean) {
    // No-op: Company selection is disabled in read-only mode
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::ChangeTicketTypePanel': typeof ChangeTicketTypePanel;
    'inbox2/change-ticket-type-panel': typeof ChangeTicketTypePanel;
  }
}
